import { inject, Injectable, Signal } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { NotificationDataInterface } from '../../interfaces/notification-data.interface';
import { AddressModel, AddressModelFromJson } from '../../models/complex/address.model';
import { TaskModel } from '../../models/complex/task.model';
import { NotificationHistoryModel } from '../../models/low-level/notification-history.model';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { NotificationModel } from '../../models/low-level/notification.model';
import { StatusModel } from '../../models/low-level/status.model';
import { Equipment } from '../../types/equipment';
import { Notification } from '../../types/notification';
import { NotificationIdentifier } from '../../types/notification-identifier';
import { NotificationStatus } from '../../types/notification-status';
import { LoadStatus } from '../../state/load-status';
import { AddressState } from '../../state/address/address-collection.state';
import { selectAddresses } from '../../state/address/selectors/address.selector';
import {
    selectEnableRemoteWork,
    selectNotificationsHistoryDaysLimit,
} from '../../state/config/selectors/config.selector';
import {
    addStatusToNotificationEntry,
    cancellationConfirmationRemoved,
    cancellationConfirmationSaved,
    initNotification,
    persistNotificationsRequested,
    setCurrentNotification,
    showNoEquipmentNotificationList,
    updateNotificationStatusFields,
} from '../../state/notification/actions/notification.actions';
import {
    entryToNotificationState,
    mainEquipmentFromNotificationState,
    NotificationAddresses,
    notificationIdToState,
    resolveNotificationAddresses,
    statusModelToStatusRecord,
    statusStateToModel,
} from '../../state/notification/notification-collection.converters';
import {
    createDefaultNotificationStatus,
    NotificationState,
} from '../../state/notification/notification-collection.state';
import { NotificationStateUtilities } from '../../state/utilities/notification-state-utlities';
import {
    selectAllSpqDocIds,
    selectAnyOtherEntryInStatusDisp,
    selectAvailableStatusForId,
    selectCancellationConfirmedById,
    selectCancelledById,
    selectCanEscalateById,
    selectCurrentNotificationStatusModel,
    selectCustomerAddressStates,
    selectEffectiveStatusById,
    selectEntriesByEquipment,
    selectEntriesInEtaStatus,
    selectEntriesInProgress,
    selectEntriesToBeSigned,
    selectEntryStatusPairsByEquipment,
    selectEntryStatusPairsInOsrOrHigher,
    selectEquipmentStates,
    selectEscalatedById,
    selectFirstEntryInProgress,
    selectFirstEntryToBeSigned,
    selectInstallationPaperworkRequired,
    selectIsMultiDay,
    selectIsTodaysEntry,
    selectNewEntries,
    selectNextUpcomingEntry,
    selectNotificationEndById,
    selectNotificationEntries,
    selectNotificationEntryById,
    selectNotificationInitialized,
    selectNotificationLoadStatus,
    selectNotificationPropertiesSnapshotById,
    selectNotificationStartById,
    selectStatusExistsForId,
    selectStatusForCodeById,
    selectTaskForCodeById,
    selectTodaysEntries,
    selectUnreadIndicatorById,
    selectUpcomingEntries,
} from '../../state/notification/selectors/notification.selector';
import {
    applyNotificationPropertiesSnapshot,
    entryToNotificationModel,
    notificationTaskToModel,
} from '../../state/notification/legacy-notification-model.bridge';
import { NotificationInterface } from '../../interfaces/notification.interface';
import { OdataNotificationService } from './odata-notification.service';

/**
 * NgRx-backed implementation of {@link NotificationDataInterface} (and the narrower
 * {@link NotificationInterface} it extends). All notification state lives in the notification
 * store slice — the postprocessed notification and the status/escalation/cancellation/unread/paused
 * data this device recorded for it alike. Every read uses a selector and every mutation dispatches an
 * action; `NotificationEffects` owns the side effects (fetching, persisting, cross-slice writes).
 * The few config values it needs come from the config slice the same way, so the service talks to
 * the store only — not to the `ConfigInterface`/`NotificationStatusInterface` tokens, whose
 * implementations would otherwise decide where the status fields it writes end up. That makes
 * the status and config slices populated too — which `enableNgRx` guarantees, since it serves them all.
 *
 * The service is a thin translation layer: model conversion happens exclusively at the public
 * method boundary via the converters in `notification-collection.converters.ts`, and the
 * `ensureModel` cache below keeps that translation cheap for the ~30 call sites that read a
 * `NotificationModel` repeatedly.
 */
@Injectable({
    providedIn: 'root',
})
export class NotificationNgrxService implements NotificationInterface, NotificationDataInterface {
    private readonly store = inject(Store);
    private readonly log: LogInterface = inject(LogInstanceToken);
    private readonly odataNotificationService = inject(OdataNotificationService);

    /**
     * One `NotificationModel` per stored notification, built on first access. 31+ call sites across
     * the app take/return `NotificationModel`; rebuilding the 500-line model on every read is not
     * viable, so the model is memoized here rather than in the slice.
     *
     * Keyed on `entry.tasks` rather than on the entry itself: a load or a hydrate builds the
     * notification's arrays afresh, while a status write copies the entry and keeps them, so this is
     * the identity that changes exactly when what the model was built from changes. Keying on the
     * entry would throw the model away several times per screen instead. Handing out one instance
     * per notification also keeps the in-place writes several callers still perform visible to each
     * other (`saveStatus` writes `updatedStatus`, `initializeNotificationStatus` and
     * `setNotificationProperties` write further fields), while the authoritative copy of each field
     * lives in the store. The cache belongs here and not in the slice: once the UI consumes state
     * types directly instead of `NotificationModel`, this service — and the cache with it — is
     * deleted.
     */
    private modelCache = new WeakMap<object, NotificationModel>();

    /**
     * The address collection the cached models were built with. A model carries the addresses its
     * notification links to, so the cache is dropped wholesale when that collection is replaced —
     * which the address slice does by reference, once per write.
     */
    private modelCacheAddresses: AddressState[] | undefined = undefined;

    /** The addresses the notifications' `customerAddressId` / `addressIds` links resolve against. */
    private readonly addresses = this.store.selectSignal(selectAddresses);

    public get isInitialized(): boolean {
        return this.store.selectSignal(selectNotificationInitialized)();
    }

    /**
     * The status of whichever notification the store currently tracks. `selectCurrentNotificationStatus`
     * composes `currentNotificationId` with the effective-status lookup and maps through the
     * state→model converter, so this is one stable signal reference rather than a per-call read.
     * Read-only (`Signal`, not `WritableSignal`): the store drives every update, so nothing here
     * ever has to `.set()` it.
     */
    public readonly notificationStatusSignal: Signal<StatusModel> = this.store.selectSignal(
        selectCurrentNotificationStatusModel,
    );

    public get customerAddresses(): AddressModel[] {
        return this.store
            .selectSignal(selectCustomerAddressStates)()
            .map((state) => new AddressModelFromJson(state));
    }

    public init(isAppStart = false): Observable<NotificationModel[]> {
        this.store.dispatch(initNotification({ isAppStart }));
        return this.store.select(selectNotificationLoadStatus).pipe(
            filter((status) => status === LoadStatus.Loaded || status === LoadStatus.Error),
            take(1),
            map(() => this.getAllNotifications()),
        );
    }

    public getStatus(notification: NotificationModel): StatusModel {
        const status = this.store.selectSignal(
            selectEffectiveStatusById(notificationIdToState(notification.identifier)),
        )();
        return status ? statusStateToModel(status) : notification.backendStatus;
    }

    public getNotificationStatusByStatusType(
        notification: NotificationModel,
        statusCode: NOTIFICATION_STATUS_CODE,
    ): StatusModel | undefined {
        const status = this.store.selectSignal(
            selectStatusForCodeById(notificationIdToState(notification.identifier), statusCode),
        )();
        return status ? statusStateToModel(status) : undefined;
    }

    public getNotificationTaskByStatusType(
        notification: NotificationModel,
        statusCode: NOTIFICATION_STATUS_CODE,
    ): TaskModel | undefined {
        const task = this.store.selectSignal(
            selectTaskForCodeById(notificationIdToState(notification.identifier), statusCode),
        )();
        return task ? notificationTaskToModel(task, notificationIdToState(notification.identifier)) : undefined;
    }

    /**
     * Appends the status to the notification unless it already carries that code, and — when it was appended — refreshes the
     * memoized model's `updatedStatus` field for the callers that read it. `notificationStatusSignal`
     * needs no refresh of its own: it reads `selectCurrentNotificationStatus`, so the write shows up
     * there as soon as the reducer commits it.
     *
     * Skipping the dispatch for a status the notification already carries is not just an
     * optimization over the reducer's own duplicate check: every dispatch triggers a persist, and a
     * redundant one has nothing to write.
     *
     * @param notification
     * @param status
     */
    public saveStatus(notification: NotificationModel, status: StatusModel): Observable<void> {
        const id = notificationIdToState(notification.identifier);
        this.log.debug(`NotificationNgrxService: saveStatus`, {
            notificationId: notification.identifier.toJSON(),
            status: status.toString(),
        });
        const isNewStatus = !this.store.selectSignal(selectStatusExistsForId(id, status.statusCode))();
        this.log.debug(`NotificationNgrxService: saveStatus: result`, {
            notificationId: notification.identifier.toJSON(),
            status: status.toString(),
            isNewStatus,
        });
        if (isNewStatus) {
            this.store.dispatch(addStatusToNotificationEntry({ id, status: statusModelToStatusRecord(status) }));
            notification.updatedStatus = status;
        }
        return of(undefined);
    }

    public getNotificationStart(notification: NotificationModel): moment.Moment {
        return (
            this.store.selectSignal(selectNotificationStartById(notificationIdToState(notification.identifier)))() ??
            notification.plannedStart
        );
    }

    public getNotificationEnd(notification: NotificationModel): moment.Moment {
        return (
            this.store.selectSignal(selectNotificationEndById(notificationIdToState(notification.identifier)))() ??
            notification.plannedEnd
        );
    }

    public isNotificationRemoteWork(notification: NotificationModel): boolean {
        return this.store.selectSignal(selectEnableRemoteWork)() && this.getStatus(notification).isRemoteWork();
    }

    public isMultiDayNotification(notification: NotificationModel): boolean {
        this.assertInitialized('isMultiDayNotification');
        return this.store.selectSignal(selectIsMultiDay(notificationIdToState(notification.identifier)))();
    }

    public isNotificationInProgress(notificationIdentifier: NotificationIdentifierModel): boolean {
        this.assertInitialized('isNotificationInProgress');
        const status = this.store.selectSignal(
            selectEffectiveStatusById(notificationIdToState(notificationIdentifier)),
        )();
        return status ? statusStateToModel(status).isInProgress() : false;
    }

    public isNotificationCancelled(notification: NotificationModel): boolean {
        return this.getCancelled(notification).length > 0;
    }

    public getEscalated(notification: NotificationModel): boolean {
        return this.store.selectSignal(selectEscalatedById(notificationIdToState(notification.identifier)))();
    }

    /**
     * Persists the escalation flag, but only for a notification whose status allows escalation at
     * all — see `selectCanEscalateById`. Escalating one that does not silently stores `false`.
     *
     * @param notification
     * @param escalated
     */
    public saveEscalated(notification: NotificationModel, escalated: boolean): Observable<void> {
        const id = notificationIdToState(notification.identifier);
        const canEscalate = this.store.selectSignal(selectCanEscalateById(id))();
        this.store.dispatch(updateNotificationStatusFields({ id, changes: { escalated: escalated && canEscalate } }));
        return of(undefined);
    }

    public getAllNotifications(): NotificationModel[] {
        this.assertInitialized('getAllNotifications');
        return this.store
            .selectSignal(selectNotificationEntries)()
            .map((entry) => this.ensureModel(entry));
    }

    public getNotificationByIdentifier(
        notificationIdentifier: NotificationIdentifierModel,
    ): NotificationModel | undefined {
        this.assertInitialized('getNotificationByIdentifier');
        const entry = this.store.selectSignal(
            selectNotificationEntryById(notificationIdToState(notificationIdentifier)),
        )();
        return entry ? this.ensureModel(entry) : undefined;
    }

    public getNotificationStateByIdentifier(notificationIdentifier: NotificationIdentifier): Notification | undefined {
        this.assertInitialized('getNotificationStateByIdentifier');
        const entry = this.store.selectSignal(selectNotificationEntryById(notificationIdentifier))();
        // Existence check ahead of the translation step, not a derived-state query:
        // `entryToNotificationState` requires a concrete entry.
        if (!entry) {
            return undefined;
        }
        const status =
            this.store.selectSignal(selectEffectiveStatusById(notificationIdentifier))() ??
            createDefaultNotificationStatus();
        return entryToNotificationState(entry, status, this.addressesFor(entry).customer);
    }

    public getStatusStateByIdentifier(notificationIdentifier: NotificationIdentifier): NotificationStatus {
        const status = this.store.selectSignal(selectEffectiveStatusById(notificationIdentifier))();
        // An existence check on the translation boundary, not a query over derived state: callers
        // pass an identifier they believe in, so an unknown one is a programming error.
        if (!status) {
            throw new Error(
                `NotificationNgrxService: getStatusStateByIdentifier: Could not find notification for identifier '${JSON.stringify(notificationIdentifier)}'`,
            );
        }
        return status;
    }

    public getMainEquipmentFromNotification(notification: Notification): Equipment | undefined {
        this.assertInitialized('getMainEquipmentFromNotification');
        return mainEquipmentFromNotificationState(notification);
    }

    public getNotificationsEquipments(): Equipment[] {
        this.assertInitialized('getNotificationsEquipments');
        return this.store.selectSignal(selectEquipmentStates)();
    }

    public getNotificationByEquipment(equipmentId: string): NotificationModel[] {
        this.assertInitialized('getNotificationByEquipment');
        return this.store
            .selectSignal(selectEntriesByEquipment(equipmentId))()
            .map((entry) => this.ensureModel(entry));
    }

    public getNotificationStateByEquipment(equipmentId: string): Notification[] {
        this.assertInitialized('getNotificationStateByEquipment');
        return this.store
            .selectSignal(selectEntryStatusPairsByEquipment(equipmentId))()
            .map(({ entry, status }) => entryToNotificationState(entry, status, this.addressesFor(entry).customer));
    }

    public getAllSPQDocIDs(): string[] {
        this.assertInitialized('getAllSPQDocIDs');
        return this.store.selectSignal(selectAllSpqDocIds)();
    }

    public getNotificationsInOSROrHigher(): Notification[] {
        return this.store
            .selectSignal(selectEntryStatusPairsInOsrOrHigher)()
            .map(({ entry, status }) => entryToNotificationState(entry, status, this.addressesFor(entry).customer));
    }

    // `Date.now()` below is read-time "now", passed straight through as a plain argument to a
    // selector that does its own comparison — not caller-side cutoff arithmetic. The reducer-owned
    // "MAX_*_MS expiry" rule doesn't apply to a read-side filter parameterized by the current time.
    public getUpcomingNotifications(): NotificationModel[] {
        this.assertInitialized('getUpcomingNotifications');
        return this.store
            .selectSignal(selectUpcomingEntries(Date.now()))()
            .map((entry) => this.ensureModel(entry));
    }

    public getNewNotifications(): NotificationModel[] {
        this.assertInitialized('getNewNotifications');
        return this.store
            .selectSignal(selectNewEntries)()
            .map((entry) => this.ensureModel(entry));
    }

    // See `getUpcomingNotifications`'s comment on `Date.now()`.
    public getTodaysNotifications(): NotificationModel[] {
        this.assertInitialized('getTodaysNotifications');
        return this.store
            .selectSignal(selectTodaysEntries(Date.now()))()
            .map((entry) => this.ensureModel(entry));
    }

    // See `getUpcomingNotifications`'s comment on `Date.now()`.
    public isTodaysNotification(notification: NotificationModel): boolean {
        return this.store.selectSignal(
            selectIsTodaysEntry(notificationIdToState(notification.identifier), Date.now()),
        )();
    }

    public getNotificationInProgress(): NotificationModel | undefined {
        const entry = this.store.selectSignal(selectFirstEntryInProgress)();
        return entry ? this.ensureModel(entry) : undefined;
    }

    public getAllNotificationsInProgress(): NotificationModel[] {
        return this.store
            .selectSignal(selectEntriesInProgress)()
            .map((entry) => this.ensureModel(entry));
    }

    public getAllNotificationsInEtaStatus(): NotificationModel[] {
        return this.store
            .selectSignal(selectEntriesInEtaStatus)()
            .map((entry) => this.ensureModel(entry));
    }

    public getNotificationToBeSigned(): NotificationModel | undefined {
        const entry = this.store.selectSignal(selectFirstEntryToBeSigned)();
        return entry ? this.ensureModel(entry) : undefined;
    }

    public getAllNotificationsToBeSigned(): NotificationModel[] {
        return this.store
            .selectSignal(selectEntriesToBeSigned)()
            .map((entry) => this.ensureModel(entry));
    }

    public displayInstallationPaperwork(notificationId: NotificationIdentifierModel): boolean {
        return this.store.selectSignal(selectInstallationPaperworkRequired(notificationIdToState(notificationId)))();
    }

    public isAnyOtherNotificationInStatusDisp(currentNotification: NotificationModel): boolean {
        return this.store.selectSignal(
            selectAnyOtherEntryInStatusDisp(notificationIdToState(currentNotification.identifier)),
        )();
    }

    public getNextUpcomingNotification(mainNotification: NotificationModel): NotificationModel | undefined {
        const entry = this.store.selectSignal(
            selectNextUpcomingEntry(notificationIdToState(mainNotification.identifier)),
        )();
        return entry ? this.ensureModel(entry) : undefined;
    }

    public getCancelled(notification: NotificationModel): string {
        return this.store.selectSignal(selectCancelledById(notificationIdToState(notification.identifier)))();
    }

    public saveCancelled(notification: NotificationModel, statusCode: NOTIFICATION_STATUS_CODE): void {
        this.store.dispatch(
            updateNotificationStatusFields({
                id: notificationIdToState(notification.identifier),
                changes: { cancelled: statusCode },
            }),
        );
    }

    // See `getUpcomingNotifications`'s comment on `Date.now()`.
    public getAvailableStatus(notification: NotificationModel): NOTIFICATION_STATUS_CODE[] {
        return this.store.selectSignal(
            selectAvailableStatusForId(notificationIdToState(notification.identifier), Date.now()),
        )();
    }

    /**
     * Sorts a caller-supplied `NotificationModel[]` by start date. Takes an arbitrary array rather
     * than slice state, so no selector fits — this is a translation-boundary operation, not a query.
     * Returns a sorted **copy** and leaves the argument untouched.
     *
     * @param notifications
     */
    public sortNotificationsByStartDate(notifications: NotificationModel[]): NotificationModel[] {
        return [...notifications].sort(
            (a, b) => this.getNotificationStart(a).valueOf() - this.getNotificationStart(b).valueOf(),
        );
    }

    public getUnreadIndicator(notification: NotificationModel): boolean {
        return this.store.selectSignal(selectUnreadIndicatorById(notificationIdToState(notification.identifier)))();
    }

    /**
     * Persists the unread flag, but only while the notification still counts as unread: once it has
     * been read, there is nothing left to record.
     *
     * @param notification
     * @param unread
     */
    public saveUnreadIndicator(notification: NotificationModel, unread: boolean): Observable<void> {
        const id = notificationIdToState(notification.identifier);
        if (this.store.selectSignal(selectUnreadIndicatorById(id))()) {
            this.store.dispatch(updateNotificationStatusFields({ id, changes: { unread } }));
        }
        return of(undefined);
    }

    /**
     * Pausing notifications is no longer supported, therefore we always say a notification is not paused.
     */
    public getPausedIndicator(): boolean {
        return false;
    }

    /**
     * Pausing notifications is no longer supported, therefore we just resolve immediately
     */
    public savePausedIndicator(): Observable<void[]> {
        return of([]);
    }

    public saveNotificationStatusModelList(): Observable<void> {
        this.store.dispatch(persistNotificationsRequested());
        return of(undefined);
    }

    public saveCancellationConfirmationForInprogress(identifier: NotificationIdentifierModel): Observable<unknown> {
        this.store.dispatch(cancellationConfirmationSaved({ id: notificationIdToState(identifier) }));
        return of(undefined);
    }

    public removeCancellationConfirmationForInprogress(): void {
        this.store.dispatch(cancellationConfirmationRemoved());
    }

    public async getCancellationConfirmationForInprogress(id: NotificationIdentifierModel): Promise<boolean> {
        return this.store.selectSignal(selectCancellationConfirmedById(notificationIdToState(id)))();
    }

    /**
     * Points {@link notificationStatusSignal} at the given notification by recording it as the
     * store's current one; from then on the signal follows that notification's status.
     *
     * @param notification
     */
    public initializeNotificationStatus(notification: NotificationModel): void {
        this.store.dispatch(setCurrentNotification({ id: notificationIdToState(notification.identifier) }));
    }

    /**
     * Paused notifications are no longer supported, therefore doing nothing
     */
    public handlePausedNotifications(): Observable<void[] | undefined> {
        return of(undefined);
    }

    public showNoEquipmentNotificationList(showAppNotification: boolean): void {
        this.store.dispatch(showNoEquipmentNotificationList({ showAppNotification }));
    }

    /**
     * Refreshes the memoized model's time window, status and flags from the store, reading them
     * through one combined selector (`selectNotificationPropertiesSnapshotById`) rather than six
     * separate ones. Nothing in the slice's own state changes, so there is no action to hang a
     * `dispatch: false` effect off and the debug log stays inline, as in `saveStatus`.
     *
     * @param notification
     */
    public setNotificationProperties(notification: NotificationModel): void {
        const snapshot = this.store.selectSignal(
            selectNotificationPropertiesSnapshotById(notificationIdToState(notification.identifier)),
        )();
        applyNotificationPropertiesSnapshot(notification, snapshot);
        this.log.debug(
            `NotificationNgrxService: setNotificationProperties for notification ${notification.identifier.toString()}`,
            snapshot,
        );
    }

    public synchronizeNotificationsCrossDevice(): Observable<NotificationModel[]> {
        this.log.error('not synchronising across devices through the service...');
        return this.store.select(selectNotificationLoadStatus).pipe(
            filter((status) => status === LoadStatus.Loaded || status === LoadStatus.Error),
            take(1),
            map(() => this.getAllNotifications()),
        );
    }

    /**
     * Whether the notification is cancelled while work on it is in flight, which the cancellation
     * dialog reads to decide which cancelled status to send.
     *
     * Answered from the store alone. The legacy implementation also consulted a
     * `confirmationStarted-<id>` localStorage marker, but that term could never change the answer: a
     * confirmation only ever exists for a notification that is already in progress, which this
     * check covers. The marker is therefore not kept at all rather than being moved into the slice.
     *
     * Still returns a promise only because the interface declares one — nothing here awaits
     * anything, and the signature can be narrowed once the legacy implementation goes.
     *
     * @param notification
     */
    public async isNotificationInProgressAndCancelledAsync(notification: NotificationModel): Promise<boolean> {
        return this.isNotificationCancelled(notification) && this.getStatus(notification).isInProgressOrToBeSigned();
    }

    /**
     * Pure OData passthrough with a config-driven day limit; no slice state is involved.
     * TODO: belongs on `OdataNotificationService`, since it still has to work once this service is
     * deleted.
     */
    public getNotificationsHistory(): Observable<NotificationHistoryModel[]> {
        return this.odataNotificationService.getNotificationsHistory(
            this.store.selectSignal(selectNotificationsHistoryDaysLimit)(),
        );
    }

    /**
     * Returns the memoized `NotificationModel` for `entry`, building and caching it on first
     * access — see the {@link modelCache} doc comment.
     *
     * @param entry
     */
    private ensureModel(entry: NotificationState): NotificationModel {
        const addresses = this.addresses();
        if (addresses !== this.modelCacheAddresses) {
            this.modelCache = new WeakMap<object, NotificationModel>();
            this.modelCacheAddresses = addresses;
        }
        const cached = this.modelCache.get(entry.tasks);
        if (cached) {
            return cached;
        }
        const { customer, addressSet } = this.addressesFor(entry);
        const model = entryToNotificationModel(entry, customer, addressSet);
        this.modelCache.set(entry.tasks, model);
        return model;
    }

    /**
     * The addresses an entry's links resolve to. A link that does not resolve is a data error — the
     * load folds every notification's address into the address slice before the notifications reach
     * it — so it is logged, and the customer address falls back to an empty one rather than leaving
     * the model without the field its readers dereference.
     *
     * @param entry
     */
    private addressesFor(entry: NotificationState): NotificationAddresses {
        const addresses = this.addresses();
        if (!addresses.some((address) => address.id === entry.customerAddressId)) {
            this.log.error(
                `NotificationNgrxService: addressesFor: no address for notification ` +
                    `${NotificationStateUtilities.notificationIdToString(entry.notificationId)} (${entry.customerAddressId})`,
            );
        }
        return resolveNotificationAddresses(entry, addresses);
    }

    /**
     * Fails loudly when a read is attempted before the notifications are available, so callers see
     * the ordering bug instead of an empty result they might mistake for "no notifications".
     * Hydration from storage satisfies `selectNotificationInitialized` too (see
     * `restoredFromStorageReducer`), which lets an offline boot with a warm cache pass the guard
     * before `init()` resolves.
     *
     * @param method
     */
    private assertInitialized(method: string): void {
        if (!this.store.selectSignal(selectNotificationInitialized)()) {
            throw new Error(
                `NotificationNgrxService: ${method}: Trying to call NotificationService before initialization!`,
            );
        }
    }
}
